import { useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import AddToDriveOutlinedIcon from '@mui/icons-material/AddToDriveOutlined';
import { DrivePicker, DrivePickerDocsView } from '@googleworkspace/drive-picker-react';
import type { PickerPickedEvent } from '@googleworkspace/drive-picker-element';

import {
  useImportGoogleDriveFilesMutation,
  useImportNativeDocsMutation,
  useImportNativeSlidesMutation,
} from '../resourcesApi';
import {
  GOOGLE_WORKSPACE_PICKER_SCOPES,
  formatGoogleWorkspaceError,
  getGoogleDrivePickerConfig,
  getGoogleWorkspaceConnectionStatus,
  getGoogleWorkspacePickerAccessToken,
  hasGoogleDrivePickerConfig,
  reconnectGoogleWorkspace,
  withGoogleWorkspaceScopes,
} from '../services/googleWorkspaceService';

import type { ResourceParentLocator } from '@/shared/types/resource';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { getAppScrollTop, setAppScrollTop } from '@/shared/utils/appScroll';

const GOOGLE_DRIVE_LINK_VIEW_IDS = ['MY_DRIVE', 'SHARED_WITH_ME', 'SHARED_DRIVES'];

interface GoogleDrivePickerButtonProps {
  locator: ResourceParentLocator;
  disabled?: boolean;
  onError?: (message: string | null) => void;
  onImportComplete?: () => void;
  /**
   * 'link' (default) attaches as a link resource; 'native' converts Google
   * Slides to editable slides; 'native-docs' converts Google Docs to editable
   * documents.
   */
  mode?: 'link' | 'native' | 'native-docs';
  onNativeImportComplete?: (result: {
    totalWarnings: number;
    importedCount: number;
    failedCount: number;
  }) => void;
  /** When true, renders as a clickable tile card instead of a Button (for use in the unified resource picker). */
  tileMode?: boolean;
}

export default function GoogleDrivePickerButton({
  locator,
  disabled = false,
  onError,
  onImportComplete,
  mode = 'link',
  onNativeImportComplete,
  tileMode = false,
}: GoogleDrivePickerButtonProps) {
  const [importGoogleDriveFiles] = useImportGoogleDriveFilesMutation();
  const [importNativeSlides] = useImportNativeSlidesMutation();
  const [importNativeDocs] = useImportNativeDocsMutation();
  const [pickerToken, setPickerToken] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [mismatchDialog, setMismatchDialog] = useState<{
    googleEmail: string;
    authEmail: string;
  } | null>(null);
  const pendingTokenRef = useRef<string | null>(null);
  const { appId, developerKey } = getGoogleDrivePickerConfig();
  const savedScrollTop = useRef(0);
  const isNativeMode = mode === 'native' || mode === 'native-docs';
  const nativeImportEnabled =
    !isNativeMode ||
    isFeatureEnabled(mode === 'native-docs' ? 'googleDocsImport' : 'googleSlidesImport');
  const importLabel = mode === 'native-docs' ? 'Import Google Docs' : 'Import Google Slides';

  // Lock body scroll while picker is open to prevent the Google Picker's
  // internal scroll manipulation from jumping the page.
  // See https://github.com/googleworkspace/drive-picker-element/issues/106
  useEffect(() => {
    if (pickerOpen) {
      savedScrollTop.current = getAppScrollTop();
      document.body.style.position = 'fixed';
      document.body.style.top = '0';
      document.body.style.width = '100%';
      setAppScrollTop(0);
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        setAppScrollTop(savedScrollTop.current);
      };
    }
  }, [pickerOpen]);

  const openPicker = (token: string) => {
    setPickerToken(token);
    setPickerOpen(true);
  };

  const handleLaunch = async () => {
    onError?.(null);

    if (!hasGoogleDrivePickerConfig()) {
      onError?.(
        'Google Drive Picker is not configured. Add VITE_GOOGLE_PICKER_API_KEY and VITE_GOOGLE_CLOUD_APP_ID to the frontend environment.'
      );
      return;
    }

    setLaunching(true);
    try {
      const tokenResult = await withGoogleWorkspaceScopes(GOOGLE_WORKSPACE_PICKER_SCOPES, () =>
        getGoogleWorkspacePickerAccessToken(GOOGLE_WORKSPACE_PICKER_SCOPES)
      );

      // Check for account mismatch before opening the picker
      let mismatch = false;
      try {
        const status = await getGoogleWorkspaceConnectionStatus(GOOGLE_WORKSPACE_PICKER_SCOPES);
        if (status.accountMismatch && status.googleEmail && status.authEmail) {
          pendingTokenRef.current = tokenResult.accessToken;
          setMismatchDialog({ googleEmail: status.googleEmail, authEmail: status.authEmail });
          mismatch = true;
        }
      } catch {
        /* non-critical, proceed to picker */
      }

      if (!mismatch) {
        openPicker(tokenResult.accessToken);
      }
    } catch (error) {
      onError?.(formatGoogleWorkspaceError(error, 'Unable to open Google Drive Picker.'));
    } finally {
      setLaunching(false);
    }
  };

  const handleMismatchContinue = () => {
    const token = pendingTokenRef.current;
    pendingTokenRef.current = null;
    setMismatchDialog(null);
    if (token) {
      openPicker(token);
    }
  };

  const handleMismatchReconnect = async () => {
    pendingTokenRef.current = null;
    setMismatchDialog(null);
    setReconnecting(true);
    try {
      await reconnectGoogleWorkspace(GOOGLE_WORKSPACE_PICKER_SCOPES);
      // After reconnecting, re-launch the full flow
      void handleLaunch();
    } catch (error) {
      onError?.(formatGoogleWorkspaceError(error, 'Unable to reconnect Google Workspace.'));
    } finally {
      setReconnecting(false);
    }
  };

  const handleClose = () => {
    setPickerOpen(false);
    setPickerToken(null);
  };

  const handlePicked = async (event: PickerPickedEvent) => {
    const fileIds = extractPickedFileIds(event.detail);
    handleClose();
    if (fileIds.length === 0) return;

    setImporting(true);
    onError?.(null);
    try {
      if (isNativeMode) {
        const importNative = mode === 'native-docs' ? importNativeDocs : importNativeSlides;
        const sourceLabel = mode === 'native-docs' ? 'Google Docs' : 'Google Slides';
        const result = await withGoogleWorkspaceScopes(GOOGLE_WORKSPACE_PICKER_SCOPES, () =>
          importNative({
            ...locator,
            fileIds,
          }).unwrap()
        );
        const importedCount = result.items.filter((i) => i.imported).length;
        const failedItems = result.items.filter((i) => !i.imported);
        const totalWarnings = result.items.reduce((sum, i) => sum + (i.warnings?.length ?? 0), 0);
        if (failedItems.length > 0) {
          const details = failedItems
            .slice(0, 3)
            .map((item) => item.error || `Unable to import ${item.title || item.fileId}.`)
            .join(' ');
          onError?.(
            importedCount > 0
              ? `Some ${sourceLabel} files could not be imported. ${details}`
              : `${sourceLabel} import failed. ${details}`
          );
        }
        onNativeImportComplete?.({ totalWarnings, importedCount, failedCount: failedItems.length });
      } else {
        await withGoogleWorkspaceScopes(GOOGLE_WORKSPACE_PICKER_SCOPES, () =>
          importGoogleDriveFiles({
            ...locator,
            fileIds,
          }).unwrap()
        );
        onImportComplete?.();
      }
    } catch (error) {
      const fallback =
        mode === 'native'
          ? 'Unable to import Google Slides presentation.'
          : mode === 'native-docs'
            ? 'Unable to import Google Docs document.'
            : 'Unable to import selected Google Drive files.';
      onError?.(formatGoogleWorkspaceError(error, fallback));
    } finally {
      setImporting(false);
    }
  };

  const buttonDisabled = disabled || launching || importing || reconnecting;

  if (!nativeImportEnabled) {
    return null;
  }

  return (
    <>
      {tileMode ? (
        <Paper
          variant="outlined"
          onClick={buttonDisabled ? undefined : () => void handleLaunch()}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: buttonDisabled ? 'default' : 'pointer',
            textAlign: 'center',
            opacity: buttonDisabled ? 0.5 : 1,
            minHeight: 100,
            '&:hover': buttonDisabled
              ? {}
              : { bgcolor: 'action.hover', borderColor: 'primary.main' },
            transition: 'border-color 0.15s, background-color 0.15s',
          }}
        >
          {launching || importing ? (
            <CircularProgress size={32} />
          ) : (
            <AddToDriveOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {launching
              ? 'Connecting...'
              : importing
                ? 'Importing...'
                : isNativeMode
                  ? importLabel
                  : 'Google Drive'}
          </Typography>
        </Paper>
      ) : (
        <Button
          size="small"
          startIcon={<AddToDriveOutlinedIcon />}
          variant="outlined"
          onClick={() => {
            void handleLaunch();
          }}
          disabled={buttonDisabled}
        >
          {launching
            ? 'Connecting...'
            : importing
              ? 'Importing...'
              : isNativeMode
                ? importLabel
                : 'Google Drive'}
        </Button>
      )}

      {pickerOpen && pickerToken && appId && developerKey && (
        <DrivePicker
          {...{
            'app-id': appId,
            'developer-key': developerKey,
            'oauth-token': pickerToken,
            origin: window.location.origin,
            multiselect: true,
            'max-items': isNativeMode ? 5 : 20,
            title:
              mode === 'native'
                ? 'Choose Google Slides to import'
                : mode === 'native-docs'
                  ? 'Choose Google Docs to import'
                  : 'Choose Google Drive resources',
          }}
          onPicked={(event) => {
            void handlePicked(event);
          }}
          onCanceled={() => handleClose()}
          onOauthError={() => {
            onError?.('Google Drive Picker authorization failed.');
            handleClose();
          }}
        >
          {mode === 'native' ? (
            <DrivePickerDocsView
              {...{
                'view-id': 'PRESENTATIONS',
                'mime-types': 'application/vnd.google-apps.presentation',
              }}
            />
          ) : mode === 'native-docs' ? (
            <DrivePickerDocsView
              {...{
                'view-id': 'DOCUMENTS',
                'mime-types': 'application/vnd.google-apps.document',
              }}
            />
          ) : (
            GOOGLE_DRIVE_LINK_VIEW_IDS.map((viewId) =>
              viewId === 'SHARED_DRIVES' ? (
                <DrivePickerDocsView
                  key={viewId}
                  {...{
                    'view-id': 'DOCS',
                    'enable-drives': 'true',
                    'include-folders': 'true',
                    mode: 'LIST',
                    'select-folder-enabled': 'true',
                  }}
                />
              ) : viewId === 'MY_DRIVE' ? (
                <DrivePickerDocsView
                  key={viewId}
                  {...{
                    'view-id': 'DOCS',
                    parent: 'root',
                    'include-folders': 'true',
                    mode: 'LIST',
                    'select-folder-enabled': 'true',
                  }}
                />
              ) : viewId === 'SHARED_WITH_ME' ? (
                <DrivePickerDocsView
                  key={viewId}
                  {...{
                    'view-id': 'DOCS',
                    'owned-by-me': 'false',
                    'include-folders': 'true',
                    mode: 'LIST',
                    'select-folder-enabled': 'true',
                  }}
                />
              ) : (
                <DrivePickerDocsView
                  key={viewId}
                  {...{
                    'view-id': viewId,
                  }}
                />
              )
            )
          )}
        </DrivePicker>
      )}

      <Dialog open={Boolean(mismatchDialog)} onClose={handleMismatchContinue}>
        <DialogTitle>Different Google account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Google Drive is connected as <strong>{mismatchDialog?.googleEmail}</strong>, but you are
            logged in as <strong>{mismatchDialog?.authEmail}</strong>. You can continue with the
            current Drive account or reconnect with a different one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              void handleMismatchReconnect();
            }}
          >
            Reconnect
          </Button>
          <Button onClick={handleMismatchContinue} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function extractPickedFileIds(detail: unknown) {
  if (!detail || typeof detail !== 'object') return [] as string[];
  const docs = (detail as { docs?: unknown }).docs;
  if (!Array.isArray(docs)) return [] as string[];

  return docs
    .map((doc) => {
      if (!doc || typeof doc !== 'object') return '';
      const record = doc as Record<string, unknown>;
      return typeof record.id === 'string'
        ? record.id
        : typeof record.ID === 'string'
          ? record.ID
          : '';
    })
    .filter((value): value is string => Boolean(value));
}
